/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Form, Input, Message } from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  API,
  copy,
  getChannelModels,
  showError,
  showInfo,
  showSuccess,
  verifyJSON,
} from '../../helpers';
import {
  CHANNEL_OPTIONS,
  channelUsesDedicatedBaseURLField,
} from '../../constants';
import { renderChannelTip } from '../../helpers/render';
import ChannelModelsModal from '../../components/ChannelModelsModal';

const MODEL_MAPPING_EXAMPLE = {
  'gpt-3.5-turbo-0301': 'gpt-3.5-turbo',
  'gpt-4-0314': 'gpt-4',
  'gpt-4-32k-0314': 'gpt-4-32k',
};

const DEFAULT_CHANNEL_CONFIG = {
  region: '',
  sk: '',
  ak: '',
  user_id: '',
  vertex_ai_project_id: '',
  vertex_ai_adc: '',
  api_version: 'v1',
};

const formatConfigJSON = (value) => JSON.stringify(value || {}, null, 2);

function type2secretPrompt(type, t) {
  switch (type) {
    case 15:
      return t('channel.edit.key_prompts.zhipu');
    case 18:
      return t('channel.edit.key_prompts.spark');
    case 22:
      return t('channel.edit.key_prompts.fastgpt');
    case 23:
      return t('channel.edit.key_prompts.tencent');
    case 52:
      return t('channel.edit.key_prompts.volcengine');
    case 53:
    case 62:
      return t('channel.edit.key_prompts.kling');
    case 54:
      return t('channel.edit.key_prompts.liblibai');
    case 56:
      return t('channel.edit.key_prompts.volcengineVoice');
    default:
      return t('channel.edit.key_prompts.default');
  }
}

const EditChannel = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const channelId = params.id;
  const isEdit = channelId !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const [showModelSwitches, setShowModelSwitches] = useState(false);
  const handleCancel = () => {
    navigate('/channel');
  };

  const originInputs = {
    name: '',
    type: 1,
    key: '',
    base_url: '',
    other: '',
    model_mapping: '',
    system_prompt: '',
    models: [],
    groups: ['default'],
    billing_multiplier: 1.0,
    model_billing_multipliers: '',
  };
  const [batch, setBatch] = useState(false);
  const [inputs, setInputs] = useState(originInputs);
  const [originModelOptions, setOriginModelOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [basicModels, setBasicModels] = useState([]);
  const [fullModels, setFullModels] = useState([]);
  const [customModel, setCustomModel] = useState('');
  const [modelMultipliers, setModelMultipliers] = useState({});
  const [multiplierSearch, setMultiplierSearch] = useState('');
  const [showOnlyConfigured, setShowOnlyConfigured] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CHANNEL_CONFIG);
  const [configJson, setConfigJson] = useState(
    formatConfigJSON(DEFAULT_CHANNEL_CONFIG)
  );
  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
    if (name === 'type') {
      let localModels = getChannelModels(value);
      if (inputs.models.length === 0) {
        setInputs((inputs) => ({ ...inputs, models: localModels }));
      }
      setBasicModels(localModels);
    }
  };

  const handleConfigChange = (e, { name, value }) => {
    setConfig((inputs) => {
      const next = { ...inputs, [name]: value };
      setConfigJson(formatConfigJSON(next));
      return next;
    });
  };

  const handleConfigJsonChange = (e, { value }) => {
    setConfigJson(value);
    if (!verifyJSON(value)) {
      return;
    }
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      setConfig(parsed);
    }
  };

  const loadChannel = async () => {
    let res = await API.get(`/api/channel/${channelId}`);
    const { success, message, data } = res.data;
    if (success) {
      if (data.models === '') {
        data.models = [];
      } else {
        data.models = data.models.split(',');
      }
      if (data.group === '') {
        data.groups = [];
      } else {
        data.groups = data.group.split(',');
      }
      if (data.model_mapping !== '') {
        data.model_mapping = JSON.stringify(
          JSON.parse(data.model_mapping),
          null,
          2
        );
      }
      if (data.model_billing_multipliers) {
        try {
          const parsed = JSON.parse(data.model_billing_multipliers);
          setModelMultipliers(parsed || {});
        } catch (e) {
          setModelMultipliers({});
        }
      } else {
        setModelMultipliers({});
      }
      if (data.billing_multiplier) {
        data.billing_multiplier = parseFloat(data.billing_multiplier);
      }
      setInputs(data);
      if (data.config !== '') {
        const parsedConfig = JSON.parse(data.config);
        setConfig(parsedConfig);
        setConfigJson(formatConfigJSON(parsedConfig));
      }
      setBasicModels(getChannelModels(data.type));
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const fetchModels = async () => {
    try {
      let res = await API.get(`/api/channel/models`);
      let localModelOptions = res.data.data.map((model) => ({
        key: model.id,
        text: model.id,
        value: model.id,
      }));
      setOriginModelOptions(localModelOptions);
      setFullModels(res.data.data.map((model) => model.id));
    } catch (error) {
      showError(error.message);
    }
  };

  const fetchGroups = async () => {
    try {
      let res = await API.get(`/api/group/`);
      setGroupOptions(
        res.data.data.map((group) => ({
          key: group,
          text: group,
          value: group,
        }))
      );
    } catch (error) {
      showError(error.message);
    }
  };

  // Channel editor initializes once from the route context.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let localModelOptions = [...originModelOptions];
    inputs.models.forEach((model) => {
      if (!localModelOptions.find((option) => option.key === model)) {
        localModelOptions.push({
          key: model,
          text: model,
          value: model,
        });
      }
    });
    setModelOptions(localModelOptions);
  }, [originModelOptions, inputs.models]);

  useEffect(() => {
    if (isEdit) {
      loadChannel().then();
    } else {
      let localModels = getChannelModels(inputs.type);
      setBasicModels(localModels);
    }
    fetchModels().then();
    fetchGroups().then();
  }, []);

  const submit = async () => {
    if (inputs.key === '') {
      if (config.ak !== '' && config.sk !== '' && config.region !== '') {
        inputs.key = `${config.ak}|${config.sk}|${config.region}`;
      } else if (
        config.region !== '' &&
        config.vertex_ai_project_id !== '' &&
        config.vertex_ai_adc !== ''
      ) {
        inputs.key = `${config.region}|${config.vertex_ai_project_id}|${config.vertex_ai_adc}`;
      }
    }
    if (!isEdit && (inputs.name === '' || inputs.key === '')) {
      showInfo(t('channel.edit.messages.name_required'));
      return;
    }
    if (inputs.type !== 43 && inputs.models.length === 0) {
      showInfo(t('channel.edit.messages.models_required'));
      return;
    }
    if (inputs.model_mapping !== '' && !verifyJSON(inputs.model_mapping)) {
      showInfo(t('channel.edit.messages.model_mapping_invalid'));
      return;
    }
    if (!verifyJSON(configJson)) {
      showInfo('高级配置 JSON 格式无效');
      return;
    }
    const parsedConfig = JSON.parse(configJson);
    if (
      !parsedConfig ||
      typeof parsedConfig !== 'object' ||
      Array.isArray(parsedConfig)
    ) {
      showInfo('高级配置 JSON 必须是对象');
      return;
    }
    // Serialize model multipliers (only include non-1.0 values)
    const filteredMultipliers = {};
    Object.keys(modelMultipliers).forEach((model) => {
      const value = parseFloat(modelMultipliers[model]);
      if (value && value !== 1.0) {
        filteredMultipliers[model] = value;
      }
    });
    inputs.model_billing_multipliers =
      Object.keys(filteredMultipliers).length > 0
        ? JSON.stringify(filteredMultipliers)
        : '';
    let localInputs = { ...inputs };
    // Model switches are saved independently; an old editor must not overwrite them.
    delete localInputs.disabled_models;
    if (localInputs.key === 'undefined|undefined|undefined') {
      localInputs.key = ''; // prevent potential bug
    }
    if (localInputs.base_url && localInputs.base_url.endsWith('/')) {
      localInputs.base_url = localInputs.base_url.slice(
        0,
        localInputs.base_url.length - 1
      );
    }
    if (localInputs.type === 3 && localInputs.other === '') {
      localInputs.other = '2024-03-01-preview';
    }
    localInputs.billing_multiplier =
      parseFloat(localInputs.billing_multiplier) || 1.0;
    let res;
    localInputs.models = localInputs.models.join(',');
    localInputs.group = localInputs.groups.join(',');
    localInputs.config = JSON.stringify(parsedConfig);
    if (isEdit) {
      res = await API.put(`/api/channel/`, {
        ...localInputs,
        id: parseInt(channelId),
      });
    } else {
      res = await API.post(`/api/channel/`, localInputs);
    }
    const { success, message } = res.data;
    if (success) {
      if (isEdit) {
        showSuccess(t('channel.edit.messages.update_success'));
      } else {
        showSuccess(t('channel.edit.messages.create_success'));
        setInputs(originInputs);
        setConfig(DEFAULT_CHANNEL_CONFIG);
        setConfigJson(formatConfigJSON(DEFAULT_CHANNEL_CONFIG));
        setModelMultipliers({});
        setCustomModel('');
        setMultiplierSearch('');
        setShowOnlyConfigured(false);
      }
    } else {
      showError(message);
    }
  };

  const addCustomModel = () => {
    if (customModel.trim() === '') return;
    if (inputs.models.includes(customModel)) return;
    let localModels = [...inputs.models];
    localModels.push(customModel);
    let localModelOptions = [];
    localModelOptions.push({
      key: customModel,
      text: customModel,
      value: customModel,
    });
    setModelOptions((modelOptions) => {
      return [...modelOptions, ...localModelOptions];
    });
    setCustomModel('');
    handleInputChange(null, { name: 'models', value: localModels });
  };

  const billingMultiplierError =
    inputs.billing_multiplier !== '' &&
    inputs.billing_multiplier !== undefined &&
    (isNaN(parseFloat(inputs.billing_multiplier)) ||
      parseFloat(inputs.billing_multiplier) < 0);

  const configuredCount = Object.keys(modelMultipliers).filter((m) => {
    const v = parseFloat(modelMultipliers[m]);
    return !isNaN(v) && v > 0;
  }).length;

  const filteredModels = (inputs.models || []).filter((model) => {
    if (
      multiplierSearch &&
      !model.toLowerCase().includes(multiplierSearch.toLowerCase())
    )
      return false;
    if (showOnlyConfigured) {
      const v = modelMultipliers[model];
      return (
        v !== undefined &&
        v !== '' &&
        !isNaN(parseFloat(v)) &&
        parseFloat(v) > 0
      );
    }
    return true;
  });

  return (
    <div className='dashboard-container'>
      {showModelSwitches && (
        <ChannelModelsModal
          channel={{ ...inputs, id: Number(channelId) }}
          onClose={() => setShowModelSwitches(false)}
          onUpdated={(data) => setInputs((previous) => ({
            ...previous,
            disabled_models: data.disabled_models,
          }))}
        />
      )}
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit
              ? t('channel.edit.title_edit')
              : t('channel.edit.title_create')}
          </Card.Header>
          <Form loading={loading} autoComplete='new-password'>
            <Form.Field>
              <Form.Select
                label={t('channel.edit.type')}
                name='type'
                required
                search
                options={CHANNEL_OPTIONS}
                value={inputs.type}
                onChange={handleInputChange}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('channel.edit.name')}
                name='name'
                placeholder={t('channel.edit.name_placeholder')}
                onChange={handleInputChange}
                value={inputs.name}
                required
              />
            </Form.Field>
            <Form.Field>
              <Form.Dropdown
                label={t('channel.edit.group')}
                placeholder={t('channel.edit.group_placeholder')}
                name='groups'
                required
                fluid
                multiple
                selection
                allowAdditions
                additionLabel={t('channel.edit.group_addition')}
                onChange={handleInputChange}
                value={inputs.groups}
                autoComplete='new-password'
                options={groupOptions}
              />
            </Form.Field>
            {renderChannelTip(inputs.type)}

            {/* Azure OpenAI specific fields */}
            {inputs.type === 3 && (
              <>
                <Message>
                  注意，<strong>模型部署名称必须和模型名称保持一致</strong>
                  ，因为 One API 会把请求体中的 model
                  参数替换为你的部署名称（模型名称中的点会被剔除），
                  <a
                    target='_blank'
                    rel='noreferrer'
                    href='https://github.com/songquanpeng/one-api/issues/133?notification_referrer_id=NT_kwDOAmJSYrM2NjIwMzI3NDgyOjM5OTk4MDUw#issuecomment-1571602271'
                  >
                    图片演示
                  </a>
                  。
                </Message>
                <Form.Field>
                  <Form.Input
                    label='AZURE_OPENAI_ENDPOINT'
                    name='base_url'
                    placeholder='请输入 AZURE_OPENAI_ENDPOINT，例如：https://docs-test-001.openai.azure.com'
                    onChange={handleInputChange}
                    value={inputs.base_url}
                    autoComplete='new-password'
                  />
                </Form.Field>
                <Form.Field>
                  <Form.Input
                    label='默认 API 版本'
                    name='other'
                    placeholder='请输入默认 API 版本，例如：2024-03-01-preview，该配置可以被实际的请求查询参数所覆盖'
                    onChange={handleInputChange}
                    value={inputs.other}
                    autoComplete='new-password'
                  />
                </Form.Field>
              </>
            )}

            {/* Custom base URL field */}
            {inputs.type === 8 && (
              <Form.Field>
                <Form.Input
                  required
                  label={t('channel.edit.proxy_url')}
                  name='base_url'
                  placeholder={t('channel.edit.proxy_url_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {channelUsesDedicatedBaseURLField(inputs.type) && (
              <Form.Field>
                <Form.Input
                  required
                  label={t('channel.edit.base_url')}
                  name='base_url'
                  placeholder={t('channel.edit.base_url_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}

            {inputs.type === 24 && (
              <Form.Field>
                <Form.Input
                  label='Version'
                  name='api_version'
                  required
                  placeholder={t('channel.edit.gemini_version_placeholder')}
                  onChange={handleConfigChange}
                  value={config.api_version}
                  autoComplete=''
                />
              </Form.Field>
            )}

            {inputs.type === 18 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.spark_version')}
                  name='other'
                  placeholder={t('channel.edit.spark_version_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 21 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.knowledge_id')}
                  name='other'
                  placeholder={t('channel.edit.knowledge_id_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 17 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.plugin_param')}
                  name='other'
                  placeholder={t('channel.edit.plugin_param_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 34 && (
              <Message>{t('channel.edit.coze_notice')}</Message>
            )}
            {inputs.type === 40 && (
              <Message>
                {t('channel.edit.douban_notice')}
                <a
                  target='_blank'
                  rel='noreferrer'
                  href='https://console.volcengine.com/ark/region:ark+cn-beijing/endpoint'
                >
                  {t('channel.edit.douban_notice_link')}
                </a>
                {t('channel.edit.douban_notice_2')}
              </Message>
            )}
            {inputs.type === 40 && (
              <Form.Group widths='equal'>
                <Form.Input
                  label='AK（仅素材管理 API 需要，可选）'
                  name='ak'
                  placeholder='火山引擎 Access Key ID'
                  onChange={handleConfigChange}
                  value={config.ak || ''}
                  autoComplete='new-password'
                />
                <Form.Input
                  label='SK（仅素材管理 API 需要，可选）'
                  name='sk'
                  placeholder='火山引擎 Secret Access Key'
                  onChange={handleConfigChange}
                  value={config.sk || ''}
                  autoComplete='new-password'
                />
              </Form.Group>
            )}
            {inputs.type !== 43 && (
              <Form.Field>
                <Form.Dropdown
                  label={t('channel.edit.models')}
                  placeholder={t('channel.edit.models_placeholder')}
                  name='models'
                  required
                  fluid
                  multiple
                  search
                  onLabelClick={(e, { value }) => {
                    copy(value).then();
                  }}
                  selection
                  onChange={handleInputChange}
                  value={inputs.models}
                  autoComplete='new-password'
                  options={modelOptions}
                />
              </Form.Field>
            )}
            {isEdit && inputs.type !== 43 && (
              <Form.Field>
                <label>模型启停</label>
                <Button
                  type='button'
                  disabled={loading}
                  onClick={() => setShowModelSwitches(true)}
                >
                  管理模型开关
                </Button>
                <span>已停用 {(inputs.disabled_models || []).length} 个模型</span>
                <p style={{ marginTop: 8, color: '#666' }}>
                  可单独启用或禁用本渠道的模型，开关立即保存。新增或删除模型请先提交渠道配置。
                </p>
              </Form.Field>
            )}
            {inputs.type !== 43 && (
              <div style={{ lineHeight: '40px', marginBottom: '12px' }}>
                <Button
                  type={'button'}
                  onClick={() => {
                    handleInputChange(null, {
                      name: 'models',
                      value: basicModels,
                    });
                  }}
                >
                  {t('channel.edit.buttons.fill_models')}
                </Button>
                <Button
                  type={'button'}
                  onClick={() => {
                    handleInputChange(null, {
                      name: 'models',
                      value: fullModels,
                    });
                  }}
                >
                  {t('channel.edit.buttons.fill_all')}
                </Button>
                <Button
                  type={'button'}
                  onClick={() => {
                    handleInputChange(null, { name: 'models', value: [] });
                  }}
                >
                  {t('channel.edit.buttons.clear')}
                </Button>
                <Input
                  action={
                    <Button type={'button'} onClick={addCustomModel}>
                      {t('channel.edit.buttons.add_custom')}
                    </Button>
                  }
                  placeholder={t('channel.edit.buttons.custom_placeholder')}
                  value={customModel}
                  onChange={(e, { value }) => {
                    setCustomModel(value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addCustomModel();
                      e.preventDefault();
                    }
                  }}
                />
              </div>
            )}
            {inputs.type !== 43 && (
              <>
                <Form.Field>
                  <Form.TextArea
                    label={t('channel.edit.model_mapping')}
                    placeholder={`${t(
                      'channel.edit.model_mapping_placeholder'
                    )}\n${JSON.stringify(MODEL_MAPPING_EXAMPLE, null, 2)}`}
                    name='model_mapping'
                    onChange={handleInputChange}
                    value={inputs.model_mapping}
                    style={{
                      minHeight: 150,
                      fontFamily: 'JetBrains Mono, Consolas',
                    }}
                    autoComplete='new-password'
                  />
                </Form.Field>
                <Form.Field>
                  <Form.TextArea
                    label={t('channel.edit.system_prompt')}
                    placeholder={t('channel.edit.system_prompt_placeholder')}
                    name='system_prompt'
                    onChange={handleInputChange}
                    value={inputs.system_prompt}
                    style={{
                      minHeight: 150,
                      fontFamily: 'JetBrains Mono, Consolas',
                    }}
                    autoComplete='new-password'
                  />
                </Form.Field>
                <Form.Field>
                  <Form.TextArea
                    label='高级配置 JSON'
                    placeholder={`{\n  "api_version": "v1",\n  "auth_type": "bearer",\n  "headers": {\n    "anthropic-version": "2023-06-01"\n  }\n}`}
                    name='config'
                    onChange={handleConfigJsonChange}
                    value={configJson}
                    style={{
                      minHeight: 150,
                      fontFamily: 'JetBrains Mono, Consolas',
                    }}
                    autoComplete='new-password'
                  />
                </Form.Field>
                {/* 计费折扣卡片 */}
                <div
                  style={{
                    border: '1px solid #E8E8E8',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    background: '#fff',
                  }}
                >
                  {/* 卡片标题 */}
                  <div
                    style={{
                      padding: '14px 16px',
                      borderBottom: '1px solid #E8E8E8',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: '600',
                        color: '#1F2937',
                        fontSize: '15px',
                      }}
                    >
                      计费折扣
                    </div>
                    <div
                      style={{
                        color: '#6B7280',
                        fontSize: '13px',
                        marginTop: '4px',
                      }}
                    >
                      统一折扣作用于该渠道全部模型；模型单独折扣会覆盖统一折扣。
                    </div>
                  </div>

                  {/* 统一折扣区 */}
                  <div
                    style={{
                      padding: '16px',
                      borderBottom: '1px solid #E8E8E8',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: '500',
                        color: '#374151',
                        fontSize: '14px',
                        marginBottom: '10px',
                      }}
                    >
                      统一折扣
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '12px',
                      }}
                    >
                      <input
                        type='number'
                        step='0.01'
                        min='0'
                        style={{
                          width: '160px',
                          padding: '7px 10px',
                          border: `1px solid ${
                            billingMultiplierError ? '#EF4444' : '#D1D5DB'
                          }`,
                          borderRadius: '4px',
                          fontSize: '14px',
                        }}
                        value={inputs.billing_multiplier}
                        onChange={(e) =>
                          handleInputChange(e, {
                            name: 'billing_multiplier',
                            value: e.target.value,
                          })
                        }
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val) && val >= 0) {
                            handleInputChange(null, {
                              name: 'billing_multiplier',
                              value: parseFloat(val.toFixed(2)),
                            });
                          }
                        }}
                        placeholder='1.00'
                      />
                      <span style={{ color: '#9CA3AF', fontSize: '12px' }}>
                        1.00 = 原价
                      </span>
                      <span style={{ color: '#9CA3AF', fontSize: '12px' }}>
                        0.90 = 9折
                      </span>
                      <span style={{ color: '#9CA3AF', fontSize: '12px' }}>
                        1.20 = 加价20%
                      </span>
                    </div>
                    {billingMultiplierError && (
                      <div
                        style={{
                          color: '#EF4444',
                          fontSize: '12px',
                          marginTop: '4px',
                        }}
                      >
                        请输入有效的非负数值
                      </div>
                    )}
                    <div
                      style={{
                        color: '#6B7280',
                        fontSize: '12px',
                        marginTop: '8px',
                      }}
                    >
                      未配置模型单独折扣时，默认使用这里的折扣
                    </div>
                  </div>

                  {/* 模型单独折扣区 */}
                  <div
                    style={{
                      padding: '16px',
                      borderBottom:
                        inputs.models && inputs.models.length > 0
                          ? '1px solid #E8E8E8'
                          : 'none',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: '500',
                        color: '#374151',
                        fontSize: '14px',
                        marginBottom: '4px',
                      }}
                    >
                      模型单独折扣
                    </div>
                    <div
                      style={{
                        color: '#6B7280',
                        fontSize: '12px',
                        marginBottom: '12px',
                      }}
                    >
                      留空表示继承统一折扣
                    </div>

                    {inputs.models && inputs.models.length > 0 ? (
                      <>
                        {/* 工具条 */}
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            marginBottom: '12px',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                          }}
                        >
                          <input
                            type='text'
                            placeholder='搜索模型'
                            value={multiplierSearch}
                            onChange={(e) =>
                              setMultiplierSearch(e.target.value)
                            }
                            style={{
                              padding: '6px 10px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '4px',
                              fontSize: '13px',
                              width: '200px',
                            }}
                          />
                          <button
                            type='button'
                            onClick={() =>
                              setShowOnlyConfigured(!showOnlyConfigured)
                            }
                            style={{
                              padding: '6px 12px',
                              border: `1px solid ${
                                showOnlyConfigured ? '#93C5FD' : '#D1D5DB'
                              }`,
                              borderRadius: '4px',
                              fontSize: '13px',
                              cursor: 'pointer',
                              background: showOnlyConfigured
                                ? '#EFF6FF'
                                : '#fff',
                              color: showOnlyConfigured ? '#1D4ED8' : '#374151',
                            }}
                          >
                            仅显示已配置
                          </button>
                          <button
                            type='button'
                            onClick={() => setModelMultipliers({})}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '4px',
                              fontSize: '13px',
                              cursor: 'pointer',
                              background: '#fff',
                              color: '#374151',
                            }}
                          >
                            清空全部单独折扣
                          </button>
                        </div>

                        {/* 表格 */}
                        <div style={{ overflowX: 'auto' }}>
                          <table
                            style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              fontSize: '13px',
                            }}
                          >
                            <thead>
                              <tr style={{ background: '#F9FAFB' }}>
                                <th
                                  style={{
                                    padding: '8px 12px',
                                    textAlign: 'left',
                                    color: '#6B7280',
                                    fontWeight: '500',
                                    borderBottom: '1px solid #E8E8E8',
                                  }}
                                >
                                  模型
                                </th>
                                <th
                                  style={{
                                    padding: '8px 12px',
                                    textAlign: 'right',
                                    color: '#6B7280',
                                    fontWeight: '500',
                                    borderBottom: '1px solid #E8E8E8',
                                    width: '90px',
                                  }}
                                >
                                  默认折扣
                                </th>
                                <th
                                  style={{
                                    padding: '8px 12px',
                                    textAlign: 'center',
                                    color: '#6B7280',
                                    fontWeight: '500',
                                    borderBottom: '1px solid #E8E8E8',
                                    width: '130px',
                                  }}
                                >
                                  单独折扣
                                </th>
                                <th
                                  style={{
                                    padding: '8px 12px',
                                    textAlign: 'right',
                                    color: '#6B7280',
                                    fontWeight: '500',
                                    borderBottom: '1px solid #E8E8E8',
                                    width: '90px',
                                  }}
                                >
                                  最终生效
                                </th>
                                <th
                                  style={{
                                    padding: '8px 12px',
                                    textAlign: 'center',
                                    color: '#6B7280',
                                    fontWeight: '500',
                                    borderBottom: '1px solid #E8E8E8',
                                    width: '70px',
                                  }}
                                >
                                  状态
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredModels.length > 0 ? (
                                filteredModels.map((model) => {
                                  const rawVal = modelMultipliers[model];
                                  const hasOverride =
                                    rawVal !== undefined &&
                                    rawVal !== '' &&
                                    !isNaN(parseFloat(rawVal)) &&
                                    parseFloat(rawVal) > 0;
                                  const defaultDiscount =
                                    parseFloat(inputs.billing_multiplier) ||
                                    1.0;
                                  const effectiveDiscount = hasOverride
                                    ? parseFloat(rawVal)
                                    : defaultDiscount;
                                  return (
                                    <tr
                                      key={model}
                                      style={{
                                        background: hasOverride
                                          ? '#EFF6FF'
                                          : 'transparent',
                                        borderBottom: '1px solid #F3F4F6',
                                      }}
                                    >
                                      <td
                                        style={{
                                          padding: '8px 12px',
                                          fontFamily: 'monospace',
                                          color: '#1F2937',
                                        }}
                                      >
                                        {model}
                                      </td>
                                      <td
                                        style={{
                                          padding: '8px 12px',
                                          textAlign: 'right',
                                          color: '#6B7280',
                                        }}
                                      >
                                        {defaultDiscount.toFixed(2)}
                                      </td>
                                      <td
                                        style={{
                                          padding: '8px 12px',
                                          textAlign: 'center',
                                        }}
                                      >
                                        <input
                                          type='number'
                                          step='0.01'
                                          min='0.01'
                                          style={{
                                            width: '90px',
                                            padding: '4px 6px',
                                            border: '1px solid #D1D5DB',
                                            borderRadius: '4px',
                                            textAlign: 'center',
                                            fontSize: '13px',
                                          }}
                                          value={
                                            rawVal !== undefined ? rawVal : ''
                                          }
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            if (v === '') {
                                              const next = {
                                                ...modelMultipliers,
                                              };
                                              delete next[model];
                                              setModelMultipliers(next);
                                            } else {
                                              setModelMultipliers({
                                                ...modelMultipliers,
                                                [model]: v,
                                              });
                                            }
                                          }}
                                          placeholder='继承'
                                        />
                                      </td>
                                      <td
                                        style={{
                                          padding: '8px 12px',
                                          textAlign: 'right',
                                          color: '#1F2937',
                                          fontWeight: hasOverride
                                            ? '500'
                                            : 'normal',
                                        }}
                                      >
                                        {effectiveDiscount.toFixed(2)}
                                      </td>
                                      <td
                                        style={{
                                          padding: '8px 12px',
                                          textAlign: 'center',
                                        }}
                                      >
                                        <span
                                          style={
                                            hasOverride
                                              ? {
                                                  background: '#DBEAFE',
                                                  color: '#1D4ED8',
                                                  padding: '2px 8px',
                                                  borderRadius: '9999px',
                                                  fontSize: '12px',
                                                  whiteSpace: 'nowrap',
                                                }
                                              : {
                                                  background: '#F3F4F6',
                                                  color: '#6B7280',
                                                  padding: '2px 8px',
                                                  borderRadius: '9999px',
                                                  fontSize: '12px',
                                                  whiteSpace: 'nowrap',
                                                }
                                          }
                                        >
                                          {hasOverride ? '覆盖' : '继承'}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td
                                    colSpan='5'
                                    style={{
                                      padding: '20px',
                                      textAlign: 'center',
                                      color: '#9CA3AF',
                                      fontSize: '13px',
                                    }}
                                  >
                                    没有匹配的模型
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div
                        style={{
                          padding: '20px',
                          textAlign: 'center',
                          color: '#9CA3AF',
                          fontSize: '13px',
                          background: '#F9FAFB',
                          borderRadius: '4px',
                        }}
                      >
                        请先选择该渠道支持的模型，再配置模型单独折扣
                      </div>
                    )}
                  </div>

                  {/* 生效预览区 */}
                  {inputs.models && inputs.models.length > 0 && (
                    <div
                      style={{
                        padding: '14px 16px',
                        background: '#F9FAFB',
                        borderRadius: '0 0 8px 8px',
                      }}
                    >
                      <div style={{ fontSize: '13px', color: '#374151' }}>
                        统一折扣：
                        <strong>
                          {(
                            parseFloat(inputs.billing_multiplier) || 1.0
                          ).toFixed(2)}
                        </strong>
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          color: '#374151',
                          marginTop: '4px',
                        }}
                      >
                        单独折扣模型：<strong>{configuredCount} 个</strong>
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          color: '#374151',
                          marginTop: '4px',
                        }}
                      >
                        继承统一折扣模型：
                        <strong>
                          {inputs.models.length - configuredCount} 个
                        </strong>
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#9CA3AF',
                          marginTop: '8px',
                        }}
                      >
                        留空的模型会自动继承统一折扣，不需要重复配置。
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            {inputs.type === 33 && (
              <Form.Field>
                <Form.Input
                  label='Region'
                  name='region'
                  required
                  placeholder={t('channel.edit.aws_region_placeholder')}
                  onChange={handleConfigChange}
                  value={config.region}
                  autoComplete=''
                />
                <Form.Input
                  label='AK'
                  name='ak'
                  required
                  placeholder={t('channel.edit.aws_ak_placeholder')}
                  onChange={handleConfigChange}
                  value={config.ak}
                  autoComplete=''
                />
                <Form.Input
                  label='SK'
                  name='sk'
                  required
                  placeholder={t('channel.edit.aws_sk_placeholder')}
                  onChange={handleConfigChange}
                  value={config.sk}
                  autoComplete=''
                />
              </Form.Field>
            )}
            {inputs.type === 42 && (
              <Form.Field>
                <Form.Input
                  label='Region'
                  name='region'
                  required
                  placeholder={t('channel.edit.vertex_region_placeholder')}
                  onChange={handleConfigChange}
                  value={config.region}
                  autoComplete=''
                />
                <Form.Input
                  label={t('channel.edit.vertex_project_id')}
                  name='vertex_ai_project_id'
                  required
                  placeholder={t('channel.edit.vertex_project_id_placeholder')}
                  onChange={handleConfigChange}
                  value={config.vertex_ai_project_id}
                  autoComplete=''
                />
                <Form.Input
                  label={t('channel.edit.vertex_credentials')}
                  name='vertex_ai_adc'
                  required
                  placeholder={t('channel.edit.vertex_credentials_placeholder')}
                  onChange={handleConfigChange}
                  value={config.vertex_ai_adc}
                  autoComplete=''
                />
              </Form.Field>
            )}
            {inputs.type === 34 && (
              <Form.Input
                label={t('channel.edit.user_id')}
                name='user_id'
                required
                placeholder={t('channel.edit.user_id_placeholder')}
                onChange={handleConfigChange}
                value={config.user_id}
                autoComplete=''
              />
            )}
            {inputs.type !== 33 &&
              inputs.type !== 42 &&
              (batch ? (
                <Form.Field>
                  <Form.TextArea
                    label={t('channel.edit.key')}
                    name='key'
                    required
                    placeholder={t('channel.edit.batch_placeholder')}
                    onChange={handleInputChange}
                    value={inputs.key}
                    style={{
                      minHeight: 150,
                      fontFamily: 'JetBrains Mono, Consolas',
                    }}
                    autoComplete='new-password'
                  />
                </Form.Field>
              ) : (
                <Form.Field>
                  <Form.Input
                    label={t('channel.edit.key')}
                    name='key'
                    required
                    placeholder={type2secretPrompt(inputs.type, t)}
                    onChange={handleInputChange}
                    value={inputs.key}
                    autoComplete='new-password'
                  />
                </Form.Field>
              ))}
            {inputs.type === 37 && (
              <Form.Field>
                <Form.Input
                  label='Account ID'
                  name='user_id'
                  required
                  placeholder={
                    '请输入 Account ID，例如：d8d7c61dbc334c32d3ced580e4bf42b4'
                  }
                  onChange={handleConfigChange}
                  value={config.user_id}
                  autoComplete=''
                />
              </Form.Field>
            )}
            {inputs.type !== 33 && !isEdit && (
              <Form.Checkbox
                checked={batch}
                label={t('channel.edit.batch')}
                name='batch'
                onChange={() => setBatch(!batch)}
              />
            )}
            {inputs.type !== 3 &&
              inputs.type !== 33 &&
              inputs.type !== 8 &&
              !channelUsesDedicatedBaseURLField(inputs.type) &&
              inputs.type !== 22 && (
                <Form.Field>
                  <Form.Input
                    label={t('channel.edit.proxy_url')}
                    name='base_url'
                    placeholder={t('channel.edit.proxy_url_placeholder')}
                    onChange={handleInputChange}
                    value={inputs.base_url}
                    autoComplete='new-password'
                  />
                </Form.Field>
              )}
            {inputs.type === 22 && (
              <Form.Field>
                <Form.Input
                  label='私有部署地址'
                  name='base_url'
                  placeholder={
                    '请输入私有部署地址，格式为：https://fastgpt.run/api/openapi'
                  }
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            <Form.Checkbox
              checked={config.support_responses === true}
              label='支持 Responses API（默认根据渠道类型自动判断，勾选可强制开启，取消勾选强制关闭）'
              name='support_responses'
              indeterminate={config.support_responses == null}
              onChange={() => {
                setConfig((prev) => {
                  // Three-state toggle: null (auto) → true → false → null
                  let next;
                  if (prev.support_responses == null) {
                    next = { ...prev, support_responses: true };
                  } else if (prev.support_responses === true) {
                    next = { ...prev, support_responses: false };
                  } else {
                    next = { ...prev };
                    delete next.support_responses;
                  }
                  setConfigJson(formatConfigJSON(next));
                  return next;
                });
              }}
            />
            <Form.Checkbox
              checked={config.support_responses_files === true}
              label='支持 Responses 文件引用（默认关闭，仅在该渠道已通过文件上传与图片编辑联调后开启）'
              name='support_responses_files'
              onChange={() => {
                setConfig((prev) => {
                  const next = {
                    ...prev,
                    support_responses_files:
                      prev.support_responses_files !== true,
                  };
                  setConfigJson(formatConfigJSON(next));
                  return next;
                });
              }}
            />
            <Form.Checkbox
              checked={config.support_responses_image_generation === true}
              label='支持 Responses 图片生成（默认关闭，仅在 image_generation 工具真实联调通过后开启）'
              name='support_responses_image_generation'
              onChange={() => {
                setConfig((prev) => {
                  const next = {
                    ...prev,
                    support_responses_image_generation:
                      prev.support_responses_image_generation !== true,
                  };
                  setConfigJson(formatConfigJSON(next));
                  return next;
                });
              }}
            />
            <Button onClick={handleCancel}>
              {t('channel.edit.buttons.cancel')}
            </Button>
            <Button
              type={isEdit ? 'button' : 'submit'}
              positive
              onClick={submit}
            >
              {t('channel.edit.buttons.submit')}
            </Button>
          </Form>
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditChannel;
